import { message, notification } from 'antd';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { I18n } from '@aws-amplify/core';

import { CreateChecklistActionPlanRequest, UpdateChecklistActionPlanRequest } from './types/request/actionPlan';
import { ActionPlanTaskDTO, ChecklistActionPlanDTO } from './types/dto/actionPlan';
import { PreliminaryAnalysisByStepName } from './types/dto/preliminaryAnalysis';
import { RebaAnglesResultsDTO, RebaResultsDTO } from './types/dto/reba';
import { CreateLineRequest, LinesRequest } from '../../ui/Inputs/types/request/line';
import { ExhibitionDTO } from './types/dto/exhibition';
import { UserDTO } from './types/dto/user';
import { FileDTO } from './types/dto/file';
import services from './services';
import { SectorDTO } from '@/components/ui/Inputs/types/response/sector';
import { WorkstationsRequest } from '@/components/ui/Inputs/types/request/workstation';

const {
	getFile,
	getReba,
	getLines,
	getTasks,
	getUsers,
	createLine,
	getSectors,
	getResults,
	setUpdateStep,
	getRebaAngles,
	getExhibitions,
	getWorkstations,
	setCalculteRisk,
	createStepDefault,
	getActionPlanByStepId,
	getFindOrCreateAnalysis,
	createChecklistActionPlan,
	updateChecklistActionPlan,
	getPreliminaryAnalysisList,
	getPreliminaryAnalysisByStepName
} = services;

const useFile = (organizationId: string, companyId: string, fileId: string) => {
	const parameters = { organizationId, companyId, fileId };
	return useQuery<FileDTO, Error>(['file', parameters], () => getFile(parameters), {
		enabled: !!organizationId && !!companyId && !!fileId
	});
};

const useSectors = (organizationId: string, companyId: string) => {
	const parameters = { organizationId, companyId };
	return useQuery<SectorDTO[], Error>(['sectors', parameters], () => getSectors(parameters), {
		enabled: !!organizationId && !!companyId
	});
};

const useLines = (organizationId: string, companyId: string, sectorId: string) => {
	const parameters: LinesRequest = { organization_id: organizationId, company_id: companyId, sector_id: sectorId };
	return useQuery(['lines', parameters], () => getLines(parameters), {
		enabled: !!organizationId && !!companyId && !!sectorId
	});
};

const useWorkstations = (organizationId: string, companyId: string, lineId: string) => {
	const parameters: WorkstationsRequest = { organization_id: organizationId, company_id: companyId, line_id: lineId };
	return useQuery(['workstations', parameters], () => getWorkstations(parameters), {
		enabled: !!organizationId && !!companyId && !!lineId
	});
};

const useRiskLevel = (organizationId: string, companyId: string, fileId: string) => {
	return useMutation((payload: any) => setCalculteRisk({ organizationId, companyId, fileId, payload }));
};

const useUpdateStep = (organizationId: string, companyId: string, fileId: string) => {
	return useMutation((payload: any) => setUpdateStep({ organizationId, companyId, fileId, payload }));
};

const usePreliminaryAnalysisList = (organizationId: string, companyId: string) => {
	const parameters = { organizationId, companyId };
	return useQuery<FileDTO[], Error>(['pea-list', parameters], () => getPreliminaryAnalysisList(parameters), {
		enabled: !!organizationId && !!companyId
	});
};

const useFindOrCreateAnalysis = (organizationId: string, companyId: string, fileId: string) => {
	const parameters = { organizationId, companyId, fileId };
	const analysis = useQuery(['preliminary-analysis', { ...parameters }], () => getFindOrCreateAnalysis(parameters), {
		refetchOnWindowFocus: true,
		enabled: !!organizationId && !!companyId && !!fileId
	});
	return {
		...analysis,
		data: analysis.data || ({} as FileDTO)
	};
};

const useGetExhibitions = (organizationId: string, companyId: string, fileId: string) => {
	const parameters = { organizationId, companyId, fileId };
	const exhibitions = useQuery(['exhibitions', parameters], () => getExhibitions(parameters), {
		enabled: !!organizationId && !!companyId
	});
	return {
		...exhibitions,
		data: exhibitions.data || ([] as ExhibitionDTO[])
	};
};

const useRebaAngles = (organizationId: string, companyId: string, fileId: string) => {
	const payload = { organizationId, companyId, fileId };
	const rebaAngles = useQuery(['reba-angles', payload], () => getRebaAngles(payload), {
		retry: 0
	});
	return {
		...rebaAngles,
		data: rebaAngles.data || ({} as RebaAnglesResultsDTO)
	};
};

const useGetResults = (organizationId: string, companyId: string, fileId: string) => {
	const parameters = { organizationId, companyId, fileId };
	return useQuery(['preliminary-analysis-results', parameters], () => getResults(parameters), {
		enabled: !!organizationId && !!companyId && !!fileId
	});
};

const useCreateActionPlan = () => {
	const queryClient = useQueryClient();
	return useMutation(({ body }: { body: CreateChecklistActionPlanRequest }) => createChecklistActionPlan(body), {
		onError: (err: any) => {
			message.error(I18n.get(err.response.data.message));
		},
		onSuccess: () => {
			queryClient.invalidateQueries(['action_plan_by_step']);
			message.success(I18n.get('Action plan created successfully'));
		}
	});
};

const useCreateLine = () => {
	const queryClient = useQueryClient();
	return useMutation(({ body }: { body: CreateLineRequest }) => createLine(body), {
		onError: (err: any) => {
			const typeError = err.response.data.details[0].split('" ');
			notification.error({
				message: `${I18n.get('Ops... something happened!')}`,
				description: `${I18n.get(typeError[1])}`
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries(['lines']);
			message.success(I18n.get('Line created successfully'));
		}
	});
};

const useUpdateActionPlan = () => {
	const queryClient = useQueryClient();
	return useMutation(({ body }: { body: UpdateChecklistActionPlanRequest }) => updateChecklistActionPlan(body), {
		onError: (err: any) => {
			message.error(I18n.get(err.response.data.message));
		},
		onSuccess: () => {
			queryClient.invalidateQueries(['action_plan_by_step']);
			message.success(I18n.get('Action plan updated successfully'));
		}
	});
};

const useFindPreliminaryAnalysisByStepName = (
	organizationId: string,
	companyId: string,
	fileId: string | undefined,
	stepName: string,
	levelRisk: number
) => {
	const parameters = { organizationId, companyId, fileId, payload: stepName };
	const file = useQuery(
		['preliminary_analysis_by_step', stepName],
		() => getPreliminaryAnalysisByStepName(parameters),
		{
			enabled: !!levelRisk
		}
	);
	return {
		...file,
		data: file.data || ({} as PreliminaryAnalysisByStepName)
	};
};

const useFindActionPlanByStepId = (stepId: string, organizationId: string, companyId: string) => {
	const actionPlan = useQuery(
		['action_plan_by_step', stepId],
		() => getActionPlanByStepId({ stepId, organizationId, companyId }),
		{
			enabled: !!stepId
		}
	);
	return {
		...actionPlan,
		data: actionPlan.data || ({} as ChecklistActionPlanDTO)
	};
};

const useFindTasks = (actionPlanId: string, organizationId: string, companyId: string) => {
	const actionPlan = useQuery(['tasks', actionPlanId], () => getTasks({ actionPlanId, organizationId, companyId }), {
		enabled: !!actionPlanId && !!organizationId && !!companyId
	});
	return {
		...actionPlan,
		data: actionPlan.data || ([] as ActionPlanTaskDTO[])
	};
};

const useGetUsers = (organizationId: string, companyId: string) => {
	const users = useQuery(
		['responsible_users', { organizationId, companyId }],
		() => getUsers({ organizationId, companyId }),
		{
			enabled: !!organizationId && !!companyId
		}
	);
	return {
		...users,
		data: users.data || ([] as UserDTO[])
	};
};

const useGetReba = (organizationId: string, companyId: string, fileId: string) => {
	const payload = { organizationId, companyId, fileId };
	const reba = useQuery(['checklist_reba', payload], () => getReba(payload));
	return {
		...reba,
		data: reba.data || ({} as RebaResultsDTO)
	};
};

const useCreateStepDefault = (organizationId: string, companyId: string, fileId: string) => {
	return useMutation(() => createStepDefault({ organizationId, companyId, fileId }));
};

export {
	useFile,
	useLines,
	useGetReba,
	useSectors,
	useGetUsers,
	useFindTasks,
	useRiskLevel,
	useUpdateStep,
	useGetResults,
	useCreateLine,
	useRebaAngles,
	useWorkstations,
	useGetExhibitions,
	useCreateActionPlan,
	useUpdateActionPlan,
	useCreateStepDefault,
	useFindOrCreateAnalysis,
	useFindActionPlanByStepId,
	usePreliminaryAnalysisList,
	useFindPreliminaryAnalysisByStepName
};
